import { UserSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/atoms/button/button';
import { EmptyState } from '../../components/molecules/empty-state/empty-state';
import { ErrorState } from '../../components/molecules/error-state/error-state';
import { Pagination } from '../../components/molecules/pagination/pagination';
import { useClientes } from '../../hooks/use-clientes';
import { ClientesCards } from './components/clientes-cards';
import { ClientesFiltersBar } from './components/clientes-filters';
import { ClientesTable } from './components/clientes-table';
import {
  CardsWrapper,
  Header,
  Page,
  SkeletonRow,
  Subtitle,
  TableWrapper,
  Title,
} from './clientes.styles';

export const ClientesPage = () => {
  const navigate = useNavigate();
  const {
    items,
    page,
    limit,
    total,
    totalPages,
    filters,
    status,
    error,
    setFilters,
    clearFilters,
    setPage,
    setLimit,
    refresh,
  } = useClientes();

  const isLoading = status === 'loading';
  const hasError = status === 'error';
  const isEmpty = !isLoading && !hasError && items.length === 0;

  const openDetail = (id: string) => navigate(`/clientes/${id}`);

  return (
    <Page>
      <Header>
        <Title>Clientes</Title>
        <Subtitle>
          Liste e consulte clientes/sellers da Vendepay. Filtre por nome,
          documento, tipo e status de KYC.
        </Subtitle>
      </Header>

      <ClientesFiltersBar
        initialValues={filters}
        onApply={setFilters}
        onClear={clearFilters}
      />

      {isLoading && (
        <div data-testid="clientes-loading">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {hasError && (
        <ErrorState
          title="Não foi possível carregar os clientes"
          description={error ?? 'Tente novamente em alguns instantes.'}
          action={
            <Button variant="secondary" onClick={() => refresh()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {isEmpty && (
        <EmptyState
          icon={<UserSquare size={26} />}
          title="Nenhum cliente encontrado"
          description="Tente ajustar os filtros ou aguarde novos cadastros."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Limpar filtros
            </Button>
          }
        />
      )}

      {!isLoading && !hasError && items.length > 0 && (
        <>
          <TableWrapper>
            <ClientesTable items={items} onOpenDetail={openDetail} />
          </TableWrapper>
          <CardsWrapper>
            <ClientesCards items={items} onOpenDetail={openDetail} />
          </CardsWrapper>
          <Pagination
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </>
      )}
    </Page>
  );
};
